import mapboxgl from "mapbox-gl";

export interface LatLng {
  latitude: number;
  longitude: number;
}

/** Ease-in-out quad — consistent deceleration curve used across all map animations */
export const easeInOutQuad = (t: number): number =>
  t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;

export interface FlyToBoundsOptions {
  /** Animation duration in ms (default: 800) */
  duration?: number;
  maxZoom?: number;
  padding?: number | mapboxgl.PaddingOptions;
}

/**
 * Smoothly animate the map to fit a collection of coordinates.
 * Replaces map.fitBounds(..., { animate: false }) with an eased transition.
 */
export function flyToBounds(
  map: mapboxgl.Map,
  points: LatLng[],
  options: FlyToBoundsOptions = {}
): void {
  if (points.length === 0) {
    return;
  }

  const { padding = 80, maxZoom = 13, duration = 800 } = options;
  const bounds = new mapboxgl.LngLatBounds();
  for (const p of points) {
    bounds.extend([p.longitude, p.latitude]);
  }

  map.fitBounds(bounds, {
    padding,
    maxZoom,
    duration,
    essential: true,
    easing: easeInOutQuad,
  });
}

export interface PanToShowPopupOptions {
  /** Animation duration in ms (default: 450) */
  duration?: number;
  /**
   * Estimated height of the popup in px (default: 220).
   * Increase if your popup content is taller.
   */
  popupHeight?: number;
  /** Extra clearance above popup top edge (default: 16) */
  topPadding?: number;
}

/**
 * Pans the map so a pin near the top of the container has enough clearance
 * for an above-pin popup to render fully within the viewport.
 *
 * Only pans if necessary — if the pin already has sufficient clearance this
 * is a no-op. The animation uses the same easing curve as flyToBounds for
 * visual consistency.
 *
 * @param map          - Mapbox GL map instance
 * @param lngLat       - [longitude, latitude] of the pin that was clicked
 * @param options      - Tuning options
 */
export function panToShowPopup(
  map: mapboxgl.Map,
  lngLat: [number, number],
  options: PanToShowPopupOptions = {}
): void {
  const { popupHeight = 220, topPadding = 16, duration = 450 } = options;

  const { y } = map.project(lngLat);

  // Popup bottom sits ~44px above the pin anchor; top is another popupHeight above that
  const minY = 44 + popupHeight + topPadding;

  if (y >= minY) {
    return; // Already enough clearance — no pan needed
  }

  // panBy([0, negative]) shifts the viewport up → pin moves down on screen
  map.panBy([0, -(minY - y)], {
    duration,
    easing: easeInOutQuad,
    essential: true,
  });
}
